import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Divider,
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Sync as SyncIcon,
  QrCode as QrCodeIcon,
  Delete as DeleteIcon,
  PowerOff as PowerOffIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { useConfirm, useToast } from '../../contexts/ConfirmContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { Lock as LockIcon, Star as StarIcon } from '@mui/icons-material';

const WhatsAppSettings: React.FC = () => {
  const confirm = useConfirm();
  const showToast = useToast();
  const navigate = useNavigate();
  const { hasActiveWhatsAppAddon } = useSubscription();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<{ instanceName: string | null; status: string; hasInstance: boolean } | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.getWhatsAppSettings();
      setSettings(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load WhatsApp settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Poll connection status while QR is displayed
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (connecting && settings && settings.status !== 'connected') {
      interval = setInterval(async () => {
        try {
          const response = await api.getWhatsAppSettings();
          setSettings(response.data);
          if (response.data.status === 'connected') {
            setConnecting(false);
            setQrCode(null);
            setPairingCode(null);
            setSuccess('WhatsApp device connected successfully!');
            clearInterval(interval);
          }
        } catch (err) {
          console.error('Status poll error:', err);
        }
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [connecting, settings]);

  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError(null);
      setSuccess(null);
      const response = await api.connectWhatsApp();
      const { qrCode, pairingCode, status } = response.data;
      setQrCode(qrCode);
      setPairingCode(pairingCode);
      setSettings(prev => prev ? { ...prev, status } : null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to initialize WhatsApp connection.');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    const isConfirmed = await confirm({
      title: 'Disconnect',
      message: 'Are you sure you want to disconnect WhatsApp?',
      severity: 'warning'
    });
    if (!isConfirmed) return;
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      await api.disconnectWhatsApp();
      setSuccess('Connection closed successfully.');
      showToast('Connection closed successfully.', 'success');
      await fetchSettings();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to disconnect.');
      showToast(err.response?.data?.message || 'Failed to disconnect.', 'error');
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const isConfirmed = await confirm({
      title: 'Delete Device Registration',
      message: 'Are you sure you want to completely delete the WhatsApp device registration? You will need to create a new device to reconnect.',
      severity: 'danger'
    });
    if (!isConfirmed) return;
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      await api.deleteWhatsApp();
      setQrCode(null);
      setPairingCode(null);
      setConnecting(false);
      setSuccess('WhatsApp integration completely cleared.');
      showToast('WhatsApp integration completely cleared.', 'success');
      await fetchSettings();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete integration record.');
      showToast(err.response?.data?.message || 'Failed to delete integration record.', 'error');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!hasActiveWhatsAppAddon) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, mb: 8, display: 'flex', justifyContent: 'center' }}>
        <Paper 
          sx={{ 
            p: 6, 
            borderRadius: 4, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.75) 0%, rgba(255, 255, 255, 0.45) 100%)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.08)',
            maxWidth: '600px',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Decorative Background Blurs */}
          <Box 
            sx={{
              position: 'absolute',
              top: -50,
              left: -50,
              width: 150,
              height: 150,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(37, 211, 102, 0.15) 0%, rgba(37, 211, 102, 0) 70%)',
              filter: 'blur(20px)',
              zIndex: 0
            }}
          />
          <Box 
            sx={{
              position: 'absolute',
              bottom: -50,
              right: -50,
              width: 150,
              height: 150,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(25, 118, 210, 0.1) 0%, rgba(25, 118, 210, 0) 70%)',
              filter: 'blur(20px)',
              zIndex: 0
            }}
          />

          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box 
              sx={{ 
                width: 80, 
                height: 80, 
                borderRadius: '50%', 
                backgroundColor: 'rgba(37, 211, 102, 0.1)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                border: '1px solid rgba(37, 211, 102, 0.2)',
                position: 'relative'
              }}
            >
              <WhatsAppIcon sx={{ color: '#25D366', fontSize: '2.5rem' }} />
              <Box 
                sx={{ 
                  position: 'absolute', 
                  bottom: -4, 
                  right: -4, 
                  backgroundColor: '#9c27b0', 
                  borderRadius: '50%', 
                  width: 28, 
                  height: 28, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  border: '2px solid #fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                <LockIcon sx={{ color: '#fff', fontSize: '1.0rem' }} />
              </Box>
            </Box>

            <Typography variant="h4" fontWeight="800" sx={{ mb: 2, color: '#1a237e', letterSpacing: '-0.5px' }}>
              Unlock WhatsApp Automation
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, px: 2, lineHeight: 1.6 }}>
              Supercharge your student communication! Unlock the premium WhatsApp module to send automated lesson reminders, schedule approvals, and direct messages using your own number.
            </Typography>

            <Grid container spacing={2} sx={{ mb: 4, textAlign: 'left' }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box display="flex" alignItems="center" gap={1.5} sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.4)', borderRadius: 2 }}>
                  <StarIcon sx={{ color: '#ffb300' }} />
                  <Typography variant="body2" fontWeight="bold">Automated Reminders</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box display="flex" alignItems="center" gap={1.5} sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.4)', borderRadius: 2 }}>
                  <StarIcon sx={{ color: '#ffb300' }} />
                  <Typography variant="body2" fontWeight="bold">DVSA Checklist Sync</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box display="flex" alignItems="center" gap={1.5} sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.4)', borderRadius: 2 }}>
                  <StarIcon sx={{ color: '#ffb300' }} />
                  <Typography variant="body2" fontWeight="bold">Your Own WhatsApp Number</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box display="flex" alignItems="center" gap={1.5} sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.4)', borderRadius: 2 }}>
                  <StarIcon sx={{ color: '#ffb300' }} />
                  <Typography variant="body2" fontWeight="bold">Zero Coding / Fast Setup</Typography>
                </Box>
              </Grid>
            </Grid>

            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/instructor/subscribe')}
              sx={{
                background: 'linear-gradient(135deg, #25D366 0%, #1ebe57 100%)',
                color: '#fff',
                fontWeight: 'bold',
                px: 5,
                py: 2,
                borderRadius: 3,
                fontSize: '1.1rem',
                boxShadow: '0 8px 20px rgba(37, 211, 102, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1ebe57 0%, #128C7E 100%)',
                  boxShadow: '0 10px 24px rgba(37, 211, 102, 0.4)',
                }
              }}
            >
              Upgrade for £5/month
            </Button>
            
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Add to your current monthly or yearly subscription
            </Typography>
          </Box>
        </Paper>
      </Container>
    );
  }

  const isConnected = settings?.status === 'connected';
  const isConnecting = settings?.status === 'connecting' || connecting;

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1a237e', mb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <WhatsAppIcon sx={{ color: '#25D366', fontSize: '2.5rem' }} /> WhatsApp Connection
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Send lesson approvals, reminders, and notifications to your students directly from your own WhatsApp number.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>Device Status</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  {isConnected ? (
                    <>
                      <CheckCircleIcon color="success" />
                      <Typography variant="subtitle1" sx={{ color: 'success.main', fontWeight: 'bold' }}>Connection Active</Typography>
                    </>
                  ) : isConnecting ? (
                    <>
                      <SyncIcon className="spin-animation" sx={{ color: '#1976d2' }} />
                      <Typography variant="subtitle1" sx={{ color: 'primary.main', fontWeight: 'bold' }}>Connecting / Waiting for QR Scan</Typography>
                    </>
                  ) : (
                    <>
                      <ErrorIcon color="error" />
                      <Typography variant="subtitle1" sx={{ color: 'error.main', fontWeight: 'bold' }}>Disconnected</Typography>
                    </>
                  )}
                </Box>
                {settings?.instanceName && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                    Device ID: <code>{settings.instanceName}</code>
                  </Typography>
                )}
              </Box>

              <Box display="flex" gap={2}>
                {!isConnected && !qrCode && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<QrCodeIcon />}
                    onClick={handleConnect}
                    sx={{ borderRadius: 2, fontWeight: 'bold', py: 1, px: 3, backgroundColor: '#25D366', '&:hover': { backgroundColor: '#1ebe57' } }}
                  >
                    {isConnecting ? 'Show QR Code' : 'Connect'}
                  </Button>
                )}

                {isConnected && (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<PowerOffIcon />}
                    onClick={handleDisconnect}
                    sx={{ borderRadius: 2, fontWeight: 'bold', py: 1, px: 2 }}
                  >
                    Disconnect
                  </Button>
                )}

                {settings?.hasInstance && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDelete}
                    sx={{ borderRadius: 2, fontWeight: 'bold', py: 1, px: 2 }}
                  >
                    Remove Device
                  </Button>
                )}
              </Box>
            </Box>

            <style>{`
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
              .spin-animation {
                animation: spin 2s linear infinite;
              }
            `}</style>

            {isConnecting && qrCode && (
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 4 }} />
                <Grid container spacing={4} alignItems="center" justifyContent="center">
                  <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      1. Scan the WhatsApp QR Code
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                      Open WhatsApp on your phone, go to the <strong>Linked Devices</strong> menu, and scan this code using the <strong>Link a Device</strong> option.
                    </Typography>
                    <Box 
                      sx={{ 
                        p: 2, 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 3, 
                        backgroundColor: '#fff', 
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        display: 'inline-block'
                      }}
                    >
                      <img 
                        src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`} 
                        alt="WhatsApp QR Code" 
                        style={{ width: 220, height: 220, display: 'block' }} 
                      />
                    </Box>
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 2, fontWeight: 'medium' }}>
                      * The QR code is valid for 40 seconds. When it expires, you can refresh it using the button below.
                    </Typography>
                  </Grid>

                  {pairingCode && (
                    <Grid size={{ xs: 12, md: 2 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Typography variant="h6" color="textSecondary" sx={{ fontWeight: 'bold' }}>OR</Typography>
                    </Grid>
                  )}

                  {pairingCode && (
                    <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        2. Pair with Phone Number
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                        Go to the WhatsApp Web / Link a Device screen on your phone, select <strong>Link with Phone Number</strong>, and enter the following code:
                      </Typography>
                      <Paper 
                        sx={{ 
                          py: 2, 
                          px: 4, 
                          backgroundColor: '#f5f5f5', 
                          border: '2px dashed #9c27b0', 
                          borderRadius: 2,
                          display: 'inline-block'
                        }}
                      >
                        <Typography variant="h5" sx={{ fontWeight: 'bold', letterSpacing: 3, color: '#9c27b0' }}>
                          {pairingCode}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
                <Box mt={4} display="flex" justifyContent="center" gap={3}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<SyncIcon />} 
                    onClick={handleConnect} 
                    sx={{ fontWeight: 'bold', borderRadius: 2, px: 3 }}
                  >
                    Refresh QR Code
                  </Button>
                  <Button 
                    variant="text" 
                    color="secondary" 
                    onClick={() => { setConnecting(false); setQrCode(null); setPairingCode(null); }} 
                    sx={{ fontWeight: 'bold' }}
                  >
                    Cancel / Go Back
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default WhatsAppSettings;
